import { useEffect, useRef } from 'react';
import { ImportJob } from '../components/ImportJobs/types';

interface PercentUpdate {
    jobId: string;
    type: 'PERCENT_UPDATE';
    percent: number;
}

export interface PermutationResult {
    state: string;
    clientId: number;
    policySetId: number;
    carrierId: number;
    versionsFound: number;
    clientName?: string;
    policySetName?: string;
    carrierName?: string;
}

export interface VersionSearchUpdate {
    requestId: string;
    type: 'VERSION_SEARCH_UPDATE';
    status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    percent: number;
    completedPermutations: number;
    totalPermutations: number;
    totalVersionsFound?: number;
    errorMessage?: string;
    completedPermutation?: PermutationResult;
}

interface JobUpdateMessage {
    type: 'connected' | 'job-update';
    accountConnectionId?: string;
    job?: ImportJob | PercentUpdate | VersionSearchUpdate;
    timestamp?: number;
}

interface UseImportJobsSSEOptions {
    accountConnectionId: string | null;
    enabled: boolean;
    onJobUpdate: (job: ImportJob) => void;
    onPercentUpdate?: (jobId: string, percent: number) => void;
    onVersionSearchUpdate?: (update: VersionSearchUpdate) => void;
    onError?: (error: Error) => void;
}

export function useImportJobsSSE({
    accountConnectionId,
    enabled,
    onJobUpdate,
    onPercentUpdate,
    onVersionSearchUpdate,
    onError
}: UseImportJobsSSEOptions) {
    const eventSourceRef = useRef<EventSource | null>(null);
    const onJobUpdateRef = useRef(onJobUpdate);
    const onPercentUpdateRef = useRef(onPercentUpdate);
    const onVersionSearchUpdateRef = useRef(onVersionSearchUpdate);
    const onErrorRef = useRef(onError);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const reconnectAttemptsRef = useRef(0);

    // Keep callback refs updated
    useEffect(() => {
        onJobUpdateRef.current = onJobUpdate;
        onPercentUpdateRef.current = onPercentUpdate;
        onVersionSearchUpdateRef.current = onVersionSearchUpdate;
        onErrorRef.current = onError;
    }, [onJobUpdate, onPercentUpdate, onVersionSearchUpdate, onError]);

    useEffect(() => {
        if (!enabled || !accountConnectionId) {
            // Clean up existing connection
            if (eventSourceRef.current) {
                console.log('SSE: Closing connection (disabled or no account)');
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            reconnectAttemptsRef.current = 0;
            return;
        }

        // console.log(`SSE: Connecting to import jobs stream for account ${accountConnectionId}`);

        const url = `/api/import-jobs/stream?accountConnectionId=${encodeURIComponent(accountConnectionId)}`;
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
            console.log('SSE: Connection opened');
            reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        };

        eventSource.onmessage = (event) => {
            try {
                const message: JobUpdateMessage = JSON.parse(event.data);

                if (message.type === 'connected') {
                    console.log('SSE: Connected to import jobs stream');
                } else if (message.type === 'job-update' && message.job) {
                    // Check if this is a VERSION_SEARCH_UPDATE message
                    if ('type' in message.job && message.job.type === 'VERSION_SEARCH_UPDATE') {
                        const searchUpdate = message.job as VersionSearchUpdate;
                        console.log('SSE: Received version search update', searchUpdate);
                        if (onVersionSearchUpdateRef.current) {
                            onVersionSearchUpdateRef.current(searchUpdate);
                        }
                    }
                    // Check if this is a PERCENT_UPDATE message
                    else if ('type' in message.job && message.job.type === 'PERCENT_UPDATE') {
                        const percentUpdate = message.job as PercentUpdate;
                        console.log('SSE: Received percent update', percentUpdate);
                        if (onPercentUpdateRef.current) {
                            onPercentUpdateRef.current(percentUpdate.jobId, percentUpdate.percent);
                        }
                    } else {
                        // Regular job update
                        console.log('SSE: Received job update', message.job);
                        onJobUpdateRef.current(message.job as ImportJob);
                    }
                }
            } catch (err) {
                console.error('SSE: Failed to parse message', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE: Connection error', err);
            eventSource.close();
            eventSourceRef.current = null;

            if (onErrorRef.current) {
                onErrorRef.current(new Error('SSE connection error'));
            }

            // Exponential backoff for reconnection
            reconnectAttemptsRef.current += 1;
            const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000); // Max 30s

            // console.log(`SSE: Attempting to reconnect in ${backoffDelay}ms (attempt ${reconnectAttemptsRef.current})`);

            // Clear any existing timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            // Schedule reconnection - don't need to do anything, the effect will re-run
            reconnectTimeoutRef.current = window.setTimeout(() => {
                // Force a re-connection by setting ref to null
                eventSourceRef.current = null;
            }, backoffDelay);
        };

        return () => {
            console.log('SSE: Cleaning up connection');
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [accountConnectionId, enabled]);

    return {
        isConnected: !!eventSourceRef.current
    };
}
